import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Payment = () => {
  const [loading, setLoading] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('esewa');
  const [paymentDetails, setPaymentDetails] = useState({
    phoneNumber: '',
    transactionId: '',
    amount: '',
    paymentProof: null
  });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get current user
    const savedUser = localStorage.getItem('user');
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      const userData = JSON.parse(savedUser);
      setCurrentUser(userData);
    } else {
      // Redirect to login if no user
      navigate('/login');
      return;
    }

    // Get selected plan from location state
    if (location.state && location.state.plan) {
      setSelectedPlan(location.state.plan);
    } else {
      // Redirect back to pricing if no plan selected
      navigate('/pricing');
    }
  }, [navigate, location.state]);

  const handlePaymentSubmission = async () => {
    if (!selectedPlan || !currentUser) return;

    // Validate payment details
    if (!paymentDetails.phoneNumber || !paymentDetails.transactionId || !paymentDetails.amount) {
      alert('Please fill in all payment details');
      return;
    }

    if (parseFloat(paymentDetails.amount) !== selectedPlan.priceNrs) {
      alert(`Amount must be exactly NRs. ${selectedPlan.priceNrs}`);
      return;
    }

    setLoading(true);
    try {
      // Get token from localStorage
      const token = currentUser.token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Create a pending payment record with payment details
      const response = await fetch('http://localhost:5000/api/payment/create', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          plan_type: selectedPlan.id,
          amount: selectedPlan.priceNrs,
          currency: 'NRs',
          duration_days: selectedPlan.id === 'trial' ? 4 : 
                        selectedPlan.id === 'quarterly' ? 90 :
                        selectedPlan.id === 'halfyearly' ? 180 : 365,
          payment_details: {
            method: selectedPaymentMethod,
            phone_number: paymentDetails.phoneNumber,
            transaction_id: paymentDetails.transactionId,
            amount_paid: paymentDetails.amount,
            submitted_at: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        setPaymentSubmitted(true);
      } else {
        alert('Failed to submit payment. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('Error submitting payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setShowPaymentForm(true);
    // Pre-fill amount
    setPaymentDetails(prev => ({
      ...prev,
      amount: selectedPlan.priceNrs.toString()
    }));
  };

  if (!selectedPlan || !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (paymentSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Submitted!</h1>
              <p className="text-lg text-gray-600">Your payment is now pending admin verification</p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-left text-blue-800 space-y-2">
                <li>• Our admin team will verify your payment within 24 hours</li>
                <li>• You will receive email confirmation once approved</li>
                <li>• Your {selectedPlan.name} subscription will be activated</li>
                <li>• You can check your subscription status in your profile</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                View Other Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
            <h1 className="text-3xl font-bold mb-2">Complete Your Payment</h1>
            <p className="text-blue-100">Secure your {selectedPlan.name} subscription</p>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Plan Details */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
                
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedPlan.name}</h3>
                      <p className="text-gray-600">{selectedPlan.description}</p>
                    </div>
                    {selectedPlan.savings && (
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {selectedPlan.savings}
                      </span>
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-semibold">{selectedPlan.period}</span>
                    </div>
                    {selectedPlan.originalPrice && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Original Price:</span>
                        <span className="text-gray-500 line-through">{selectedPlan.originalPrice}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total Amount:</span>
                      <span className="text-blue-600">{selectedPlan.price}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Features Included:</h4>
                  <ul className="text-blue-800 space-y-1">
                    {selectedPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Payment Method</h2>
                
                {!showPaymentForm ? (
                  <div className="space-y-4 mb-6">
                    {/* eSewa */}
                    <div 
                      onClick={() => handlePaymentMethodSelect('esewa')}
                      className="border-2 border-green-200 rounded-lg p-4 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-8 bg-green-600 rounded flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">eSewa</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">eSewa Digital Wallet</h3>
                          <p className="text-sm text-gray-600">Pay securely with eSewa</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <button className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700">
                          Pay with eSewa
                        </button>
                      </div>
                    </div>

                    {/* Khalti */}
                    <div 
                      onClick={() => handlePaymentMethodSelect('khalti')}
                      className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-8 bg-purple-600 rounded flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-xs">Khalti</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Khalti Digital Wallet</h3>
                          <p className="text-sm text-gray-600">Pay with Khalti wallet</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <button className="bg-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-purple-700">
                          Pay with Khalti
                        </button>
                      </div>
                    </div>

                    {/* Bank Transfer */}
                    <div 
                      onClick={() => handlePaymentMethodSelect('bank')}
                      className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-xs">BANK</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Bank Transfer</h3>
                          <p className="text-sm text-gray-600">Direct bank transfer</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <button className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700">
                          Pay via Bank Transfer
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Payment Form */
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded mr-3 flex items-center justify-center ${
                          selectedPaymentMethod === 'esewa' ? 'bg-green-600' :
                          selectedPaymentMethod === 'khalti' ? 'bg-purple-600' : 'bg-blue-600'
                        }`}>
                          <span className="text-white font-bold text-xs">
                            {selectedPaymentMethod === 'esewa' ? 'eS' :
                             selectedPaymentMethod === 'khalti' ? 'Kh' : 'BK'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 capitalize">
                            {selectedPaymentMethod === 'esewa' ? 'eSewa' :
                             selectedPaymentMethod === 'khalti' ? 'Khalti' : 'Bank Transfer'} Payment
                          </h3>
                          <p className="text-sm text-gray-600">Complete your payment details</p>
                        </div>
                        <button 
                          onClick={() => setShowPaymentForm(false)}
                          className="ml-auto text-gray-500 hover:text-gray-700"
                        >
                          ← Back
                        </button>
                      </div>
                    </div>

                    {/* Payment Details Form */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {selectedPaymentMethod === 'bank' ? 'Account Number' : 'Phone Number'}
                        </label>
                        <input
                          type="text"
                          value={paymentDetails.phoneNumber}
                          onChange={(e) => setPaymentDetails(prev => ({...prev, phoneNumber: e.target.value}))}
                          placeholder={selectedPaymentMethod === 'bank' ? 'Enter your account number' : 'Enter your phone number'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          value={paymentDetails.transactionId}
                          onChange={(e) => setPaymentDetails(prev => ({...prev, transactionId: e.target.value}))}
                          placeholder="Enter transaction ID from your payment"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount Paid (NRs.)
                        </label>
                        <input
                          type="number"
                          value={paymentDetails.amount}
                          onChange={(e) => setPaymentDetails(prev => ({...prev, amount: e.target.value}))}
                          placeholder={`Enter ${selectedPlan.priceNrs}`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-sm text-gray-600 mt-1">
                          Expected amount: NRs. {selectedPlan.priceNrs}
                        </p>
                      </div>

                      {selectedPaymentMethod !== 'bank' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Payment Instructions:</h4>
                          <ol className="text-blue-800 text-sm space-y-1">
                            <li>1. Open your {selectedPaymentMethod === 'esewa' ? 'eSewa' : 'Khalti'} app</li>
                            <li>2. Send NRs. {selectedPlan.priceNrs} to: <strong>9841234567</strong></li>
                            <li>3. Copy the transaction ID from the confirmation</li>
                            <li>4. Enter the details above and submit</li>
                          </ol>
                        </div>
                      )}

                      {selectedPaymentMethod === 'bank' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Bank Details:</h4>
                          <div className="text-blue-800 text-sm space-y-1">
                            <p><strong>Bank:</strong> Nepal Investment Bank</p>
                            <p><strong>Account:</strong> 1234567890</p>
                            <p><strong>Name:</strong> MathVenture Education</p>
                            <p><strong>Amount:</strong> NRs. {selectedPlan.priceNrs}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-yellow-800">Payment Verification</h4>
                          <p className="text-yellow-700 text-sm mt-1">
                            After submitting, our admin team will verify your payment within 24 hours and activate your subscription.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handlePaymentSubmission}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Submitting Payment...
                        </div>
                      ) : (
                        'Submit Payment for Verification'
                      )}
                    </button>
                  </div>
                )}

                {!showPaymentForm && (
                  <p className="text-xs text-gray-600 text-center mt-4">
                    Choose your preferred payment method to continue
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;